import type { ProductPublicationStatus, ProductStockStatus, Currency } from '../../../constants';
import type { Product, TaxonomyTerm, ProductDetailData, ProductMeta } from '../../../model';

export type ProductsCollectionRequest = {
  collectionName: string;
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency: Currency;
  categories?: string;
};

export type ProductsCollectionResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
};

export type ProductsAllRequest = {
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency: Currency;
};

export type ProductsAllResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
};

export type ProductsByIdsRequest = {
  /**
   * Comma-separated list of product IDs (e.g., "123" or "123,456,789")
   */
  ids: string;
  currency: Currency;
};

export type ProductsDetailRequest = {
  /**
   * Product Slug (e.g., "snow-flower-whipping-cleanser-150ml-1ea")
   */
  slug: string;
  currency: Currency;
};

export type ProductMetaRequest = {
  /**
   * Product ID (e.g., "123")
   */
  productId: string;
};

export type ProductsByIdsResponse = Product[];
export type ProductsDetailResponse = ProductDetailData;
export type ProductMetaResponse = ProductMeta;

export type TopRankedProductsRequest = {
  collectionName?: string;
  page?: number;
  limit?: number;
  currency: Currency;
  categories?: string; // Added back for backward compatibility
};

export type TopRankedProductsResponse = {
  products: Product[];
};

export type ProductsCollectionCompetitorRequest = {
  competitorName: string; // Reverted to original field name
  collectionName?: string; // Added for backward compatibility
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency?: Currency;
  categories?: string;
};

export type ProductsCollectionCompetitorResponse = {
  products: Product[];
};

export type ProductTaxonomy = {
  skinType: TaxonomyTerm[];
  skinConcern: TaxonomyTerm[];
  ingredient: TaxonomyTerm[];
};

export type ProductBrandTaxonomy = TaxonomyTerm[];

export type CategoryTaxonomy = TaxonomyTerm[];

export type ProductBrowseRequest = {
  publicationStatus?: 'publish' | 'draft';
  stockStatus?: 'instock' | 'outofstock';
  currency: Currency;
  categories?: string;
  brands?: string;
  skinTypes?: string;
  skinConcerns?: string;
  ingredients?: string;
  page?: number;
  limit?: number;
  // New API fields
  sortBy?: string;
  minPrice?: number;
  maxPrice?: number;
};

/**
 * Filter summary item from API response
 * Used for dynamic filter options with product counts
 */
export type FilterSummaryItem = {
  termId: number;
  name: string;
  count: number;
};

/**
 * Filter summary data from API response
 * Contains available filter options with counts
 */
export type FilterSummary = {
  brand: FilterSummaryItem[];
  skinType: FilterSummaryItem[];
  skinConcern: FilterSummaryItem[];
  ingredient: FilterSummaryItem[];
};

export type ProductBrowseResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  // New API field - filter summary for dynamic filter options
  summary: FilterSummary;
};

export type ProductSearchRequest = ProductBrowseRequest & {
  q: string; // Required search query parameter
};

export type ProductSearchResponse = ProductBrowseResponse & {
  searchQuery: string; // The search query that was executed
};
